import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Mail, Send, Eye, ShoppingCart, Globe, Users, CheckCircle, 
  AlertCircle, Loader2, Play, FileText, BarChart3, Plus,
  Upload, Search, Edit2, Save, X, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MarketEmailCampaign = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLanguage, setPreviewLanguage] = useState('tr');
  const [testEmail, setTestEmail] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  
  // Market list management
  const [markets, setMarkets] = useState([]);
  const [marketSearch, setMarketSearch] = useState('');
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [editingMarket, setEditingMarket] = useState(null);
  const [newEmailValue, setNewEmailValue] = useState('');
  
  // Bulk email import
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);

  // Language options with flags
  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷', videoAvailable: true },
    { code: 'en', name: 'English', flag: '🇬🇧', videoAvailable: true },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪', videoAvailable: true },
    { code: 'fr', name: 'Français', flag: '🇫🇷', videoAvailable: true },
    { code: 'ar', name: 'العربية', flag: '🇸🇦', videoAvailable: false },
    { code: 'ru', name: 'Русский', flag: '🇷🇺', videoAvailable: false },
    { code: 'fa', name: 'فارسی', flag: '🇮🇷', videoAvailable: false },
  ];

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchPreview(previewLanguage);
  }, [previewLanguage]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/market-emails/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await api.get('/admin/market-emails/logs');
      setEmailLogs(res.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const fetchPreview = async (lang) => {
    try {
      const res = await api.post(`/admin/market-emails/preview?language=${lang}`);
      setPreviewHtml(res.data.html);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
    }
  };

  const searchMarkets = async () => {
    if (!marketSearch.trim()) return;
    
    setLoadingMarkets(true);
    try {
      const res = await api.get(`/markets?q=${encodeURIComponent(marketSearch)}&limit=50`);
      setMarkets(res.data.markets || []);
    } catch (error) {
      console.error('Failed to search markets:', error);
      toast.error('Arama başarısız');
    } finally {
      setLoadingMarkets(false);
    }
  };

  const updateMarketEmail = async (marketId) => {
    try {
      await api.put(`/admin/markets/${marketId}/email`, { email: newEmailValue });
      toast.success('Email güncellendi');
      setEditingMarket(null);
      setNewEmailValue('');
      searchMarkets();
    } catch (error) {
      console.error('Failed to update email:', error);
      toast.error('Güncelleme başarısız');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Test email adresi girin');
      return;
    }

    setSendingEmail(true);
    try {
      await api.post('/admin/market-emails/send', {
        language: previewLanguage,
        test_email: testEmail,
        limit: 1
      });
      toast.success('Test email gönderildi');
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error('Email gönderilemedi');
    } finally {
      setSendingEmail(false);
    }
  };

  const sendCampaignEmails = async () => {
    if (!window.confirm(`${stats?.with_email || 0} markete email göndermek istediğinize emin misiniz?`)) {
      return;
    }

    setSendingEmail(true);
    try {
      const res = await api.post('/admin/market-emails/send', {
        language: previewLanguage,
        limit: 100
      });
      toast.success(res.data.message);
      fetchLogs();
    } catch (error) {
      console.error('Failed to send campaign:', error);
      toast.error('Kampanya gönderilemedi');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleBulkImport = async (file) => {
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/admin/market-emails/bulk-update', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${res.data.updated} market güncellendi`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} hata oluştu`);
      }
      setShowBulkImportDialog(false);
      fetchStats();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('İçe aktarma başarısız');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="w-7 h-7 text-green-600" />
            Market E-posta Kampanyası
          </h2>
          <p className="text-gray-500 mt-1">Türk marketlerine davet e-postası gönderin</p>
        </div>
        <Button onClick={() => setShowBulkImportDialog(true)} variant="outline">
          <Upload className="w-4 h-4 mr-2" />
          Toplu Email İçe Aktar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Toplam Market</p>
                <p className="text-3xl font-bold text-gray-900">{stats?.total_markets || 0}</p>
              </div>
              <ShoppingCart className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Email Mevcut</p>
                <p className="text-3xl font-bold text-green-600">{stats?.with_email || 0}</p>
              </div>
              <Mail className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Email Eksik</p>
                <p className="text-3xl font-bold text-orange-600">{stats?.without_email || 0}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ülke Sayısı</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.by_country?.length || 0}</p>
              </div>
              <Globe className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="preview" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="preview">
            <Eye className="w-4 h-4 mr-2" />
            Önizleme
          </TabsTrigger>
          <TabsTrigger value="send">
            <Send className="w-4 h-4 mr-2" />
            Gönder
          </TabsTrigger>
          <TabsTrigger value="markets">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Marketler
          </TabsTrigger>
          <TabsTrigger value="logs">
            <BarChart3 className="w-4 h-4 mr-2" />
            Geçmiş
          </TabsTrigger>
        </TabsList>

        {/* Preview Tab */}
        <TabsContent value="preview" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Önizleme</CardTitle>
              <CardDescription>Gönderilecek email şablonunu önizleyin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <Select value={previewLanguage} onValueChange={setPreviewLanguage}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          {lang.flag} {lang.name}
                          {lang.videoAvailable && <Badge variant="outline" className="text-xs">Video</Badge>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button 
                  variant="outline"
                  onClick={() => window.open(`${API_URL}/api/static/videos/market_promo_${previewLanguage}.mp4`, '_blank')}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Video İzle
                </Button>
              </div>

              <div className="border rounded-lg overflow-hidden bg-gray-50">
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-[600px]"
                  title="Email Preview"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Send Tab */}
        <TabsContent value="send" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Test Email Gönder</CardTitle>
                <CardDescription>Önce kendinize test email gönderin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Test Email Adresi</Label>
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@email.com"
                  />
                </div>
                <Button 
                  onClick={sendTestEmail} 
                  disabled={sendingEmail}
                  className="w-full"
                >
                  {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Test Gönder
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Kampanya Başlat</CardTitle>
                <CardDescription>Tüm marketlere email gönderin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>{stats?.with_email || 0}</strong> markete email gönderilebilir
                  </p>
                </div>
                <Button 
                  onClick={sendCampaignEmails} 
                  disabled={sendingEmail || !stats?.with_email}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Kampanyayı Başlat
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Markets Tab */}
        <TabsContent value="markets" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Market Listesi</CardTitle>
              <CardDescription>Market email adreslerini yönetin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="Market adı veya şehir ara..."
                  value={marketSearch}
                  onChange={(e) => setMarketSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchMarkets()}
                />
                <Button onClick={searchMarkets} disabled={loadingMarkets}>
                  {loadingMarkets ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {markets.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Market</TableHead>
                      <TableHead>Şehir</TableHead>
                      <TableHead>Ülke</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="w-20">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {markets.map(market => (
                      <TableRow key={market.id}>
                        <TableCell className="font-medium">{market.name}</TableCell>
                        <TableCell>{market.city}</TableCell>
                        <TableCell>{market.country}</TableCell>
                        <TableCell>
                          {editingMarket === market.id ? (
                            <div className="flex gap-2">
                              <Input
                                value={newEmailValue}
                                onChange={(e) => setNewEmailValue(e.target.value)}
                                placeholder="email@market.com"
                                className="h-8"
                              />
                              <Button size="sm" onClick={() => updateMarketEmail(market.id)}>
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingMarket(null)}>
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <span className={market.email ? 'text-green-600' : 'text-gray-400'}>
                              {market.email || 'Yok'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingMarket !== market.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingMarket(market.id);
                                setNewEmailValue(market.email || '');
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Kampanya Geçmişi</CardTitle>
              <CardDescription>Gönderilen email kampanyaları</CardDescription>
            </CardHeader>
            <CardContent>
              {emailLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Henüz kampanya gönderilmedi</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Dil</TableHead>
                      <TableHead>Gönderilen</TableHead>
                      <TableHead>Başarısız</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLogs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{new Date(log.sent_at).toLocaleString('tr-TR')}</TableCell>
                        <TableCell>{log.language}</TableCell>
                        <TableCell className="text-green-600">{log.total_sent}</TableCell>
                        <TableCell className="text-red-600">{log.total_failed}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Toplu Email İçe Aktar</DialogTitle>
            <DialogDescription>
              CSV dosyası yükleyin. Sütunlar: name, city, email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => handleBulkImport(e.target.files?.[0])}
              disabled={importing}
            />
            {importing && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                İçe aktarılıyor...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketEmailCampaign;
